/**
 * GP HTL Dashboard Tab
 * Shows summary cards, payment chain status, and recent activity
 */
import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  FlagCircle as MilestoneIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useGphtlDashboard, useGphtlRecentActivity } from '../../../../../hooks/useGphtl';
import { GphtlActivity } from '../../../../../types/gphtl.types';

// Format currency for display
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format date for display
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Get activity icon based on type
const getActivityIcon = (activityType: string) => {
  switch (activityType) {
    case 'deal_flagged':
      return <AddIcon color="primary" />;
    case 'invoice_created':
      return <ReceiptIcon color="info" />;
    case 'bill_created':
      return <ReceiptIcon color="warning" />;
    case 'payment_received':
      return <PaymentIcon color="success" />;
    case 'milestone_achieved':
      return <MilestoneIcon color="success" />;
    default:
      return <BusinessIcon />;
  }
};

interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ color: color || 'text.primary' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: color || 'primary.main', opacity: 0.7 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

export const GphtlDashboardTab: React.FC = () => {
  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useGphtlDashboard();
  const { data: activities, isLoading: activitiesLoading } = useGphtlRecentActivity(10);

  if (dashboardLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (dashboardError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load dashboard data: {dashboardError.message}
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No GP HTL data available yet.
      </Alert>
    );
  }

  const { paymentChainStatus, equityEarned, totalEquityPossible } = dashboardData;
  const totalChains = (paymentChainStatus?.complete || 0) + (paymentChainStatus?.partial || 0) + (paymentChainStatus?.pending || 0);
  const equityProgress = totalEquityPossible > 0 ? (equityEarned / totalEquityPossible) * 100 : 0;

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active Deals"
            value={dashboardData.activeDeals}
            subtitle={`+${dashboardData.dealsThisMonth} this month`}
            icon={<BusinessIcon fontSize="large" />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Revenue"
            value={formatCurrency(dashboardData.totalRevenue)}
            subtitle={`+${formatCurrency(dashboardData.revenueMTD)} MTD`}
            icon={<TrendingUpIcon fontSize="large" />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Pending Bills"
            value={formatCurrency(dashboardData.pendingBillsAmount)}
            subtitle={`${dashboardData.pendingBillsCount} outstanding`}
            icon={<ReceiptIcon fontSize="large" />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Equity Earned"
            value={`${equityEarned.toFixed(2)}%`}
            subtitle={`of ${totalEquityPossible}%`}
            icon={<WalletIcon fontSize="large" />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Payment Chain Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Chain Status
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {totalChains === 0 ? (
                <Typography color="text.secondary">No payment chains recorded yet.</Typography>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckIcon color="success" sx={{ mr: 1 }} />
                    <Typography sx={{ flex: 1 }}>
                      Complete (Customer→Horwath→Barrett→GPHTL paid)
                    </Typography>
                    <Chip label={paymentChainStatus.complete || 0} color="success" size="small" />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                    <Typography sx={{ flex: 1 }}>Partial (Some payments received)</Typography>
                    <Chip label={paymentChainStatus.partial || 0} color="warning" size="small" />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PendingIcon color="info" sx={{ mr: 1 }} />
                    <Typography sx={{ flex: 1 }}>Pending (Awaiting customer payment)</Typography>
                    <Chip label={paymentChainStatus.pending || 0} color="info" size="small" />
                  </Box>

                  {(paymentChainStatus.error || 0) > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WarningIcon color="error" sx={{ mr: 1 }} />
                      <Typography sx={{ flex: 1 }}>Error (Requires attention)</Typography>
                      <Chip label={paymentChainStatus.error} color="error" size="small" />
                    </Box>
                  )}
                </Box>
              )}

              {/* Equity Progress */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Equity Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={equityProgress}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {equityProgress.toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {equityEarned.toFixed(2)}% earned of {totalEquityPossible}% total possible
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {activitiesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !activities || activities.length === 0 ? (
                <Typography color="text.secondary">No recent activity.</Typography>
              ) : (
                <List dense disablePadding>
                  {activities.map((activity: GphtlActivity, index: number) => (
                    <React.Fragment key={activity.id || index}>
                      <ListItem disablePadding sx={{ py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getActivityIcon(activity.activity_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.description}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{activity.deal_name}</span>
                              <span>{formatDate(activity.created_at)}</span>
                            </Box>
                          }
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                      {index < activities.length - 1 && <Divider variant="inset" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GphtlDashboardTab;
